import {UI} from "../stem-core/ui/UIBase";
import {AuthHelper} from "./AuthHelper";
import {authService} from "../client/connection/services/AuthService.js";
import {DonationOffer, PaymentMethod} from "./State";
import {apiClient} from "../client/connection/BlinkApiClient.js";
import {apiCancelDonation, type DonateRequest} from "../client/state/DonationStore";
import {apiCancelSubscription, apiSubscribe, type SubscribeRequest} from "../client/state/SubscriptionStore";
import {PaymentProcessor} from "../client/state/PaymentProcessorStore.js";
import {IS_PRODUCTION, TEST_MODE} from "../blink-sdk/Constants.js";
import {Flow} from "./panel/flows/Flow.js";
import type {StoreId} from "../stem-core/state/State";

// Assigned onto the global object below, for debugging or for a third-party script to find
declare global {
    interface Window {
        AutomationHelper: any;
        STEM_DEBUG: boolean;
    }
}

// testId isn't a stem attribute - it's registered on the shared domAttributesMap below, which makes
// it valid on every element in the app. Declared here so the type follows the registration.
declare module "../stem-core/ui/UIBase" {
    interface UIElementOptions {
        testId?: string;
    }
}


if (!IS_PRODUCTION || TEST_MODE) {
    self.STEM_DEBUG = true;
    // For non-production builds we insert the testId attribute directly into the DOM
    UI.Element.domAttributesMap.setAttribute("testId", {domName: "test-id"});
}


// TODO This needs to be only in debug/test builds
class AutomationHelperClass {
    async authenticate(userDetails = {email: "", firstName: "", lastName: ""}) {
        const automationEmailCode = "434343";
        const payload = {
            ...userDetails,
            withSignup: true,
            secure: false,
        };
        const response = await AuthHelper.requestLoginCode(payload);
        if (response.token) {
            return response;
        }
        return AuthHelper.loginWithEmailCode(automationEmailCode);
    }

    async addCard(cardDetails: Record<string, any>) {
        const request = {
            paymentProcessorId: PaymentProcessor.findBy({isPublic: true}).id,
            ...cardDetails,
        }
        return apiClient.post("/payment_methods/dev_add_payment_method", request);
    }

    async donate(payload: DonateRequest) {
        const donationOffer = DonationOffer.all().find(offer => offer.id === payload.recurringOfferId);
        if (payload.preferredPaymentMethodId != null) {
            Flow.paymentMethod.update({
                selectedValue: PaymentMethod.get(payload.preferredPaymentMethodId),
            });
        }
        if (!donationOffer) {
            throw new Error(`Donation offer with id ${payload.recurringOfferId} not found.`);
        }
        return Flow.donation.callDonateEndpoint(payload);
    }

    async cancelDonation(donationId: StoreId) {
        return apiCancelDonation({id: donationId});
    }

    async subscribe(subscriptionDetails: SubscribeRequest) {
        return apiSubscribe(subscriptionDetails);
    }

    async cancelSubscription(subscriptionId: StoreId) {
        return apiCancelSubscription(subscriptionId);
    }

    async addAddress(addressDetails: Record<string, any>) {
        return apiClient.post("/user_addresses/create", addressDetails);
    }

    async logout() {
        return authService.logout();
    }
}

export const AutomationHelper = new AutomationHelperClass();
window.AutomationHelper = AutomationHelper;
